import React, { useState, useEffect, useMemo } from "react";
import { Card, Table, Form, Col } from "react-bootstrap";
import { apiService } from "service/ApiService";
import { notificationService } from "service/NotificationService";
import { ClipLoader } from "react-spinners";

import { useTranslation } from "react-i18next";

function StoreProductsTable({ storeId, onRowSelect, selectedIntegration, refresh, sendMatchedIntegrationIds, selectedRowId }) {
  const {
    t,
  } = useTranslation();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState({ id: "", category: "", name: "", price: "", integratorId: "" });
  const [showLoader, setShowLoader] = useState(true);

  const handleSearch = (event, column) => {
    setSearch({ ...search, [column]: event.target.value });
  };

  const filteredData = data.filter((row) => {
    return (
      row.id.toString().startsWith(search.id) &&
      row.category.name.toLowerCase().includes(search.category.toLowerCase()) &&
      row.name.toLowerCase().includes(search.name.toLowerCase()) &&
      row.price.toString().includes(search.price) &&
      row.deleted === false
    );
  });

  const getData = async () => {
    setShowLoader(true);
    try {
      const dataResponse = await apiService.get(`api/v1/products/store/${storeId}`);
      setData(dataResponse);
    } catch (error) {
      console.error("Error fetching data:", error);
      notificationService.notifyWarn(error.message);
    } finally {
      setShowLoader(false);
    }
  };

  const handleRowClick = (id) => {
    if (selectedRowId === id) {
      onRowSelect(null);
      sendMatchedIntegrationIds([]);
    } else {
      onRowSelect(id);
      const myProduct = data.find((el) => el.id === id);
      if (!myProduct) {
        console.error(`Cannot find clicked product!`);
        sendMatchedIntegrationIds([]);
        return;
      }
      let integratorIds = [];
      switch (selectedIntegration) {
        case 1:
          integratorIds = myProduct.efoodId ? myProduct.efoodId.split(";") : [];
          break;
        case 2:
          integratorIds = myProduct.woltId ? myProduct.woltId.split(";") : [];
          break;
        case 3:
          integratorIds = myProduct.boxId ? myProduct.boxId.split(";") : [];
          break;
        case 5:
          integratorIds = myProduct.gonnaOrderId ? myProduct.gonnaOrderId.split(";") : [];
          break;
        case 6:
          integratorIds = myProduct.deliveryManagerId ? myProduct.deliveryManagerId.split(";") : [];
          break;
        case 7:
          integratorIds = myProduct.fluterId ? myProduct.fluterId.split(";") : [];
          break;
        case 11:
          integratorIds = myProduct.culizenId ? myProduct.culizenId.split(";") : [];
          break;
      }
      sendMatchedIntegrationIds(integratorIds);
    }
  };

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const aMatches =
        selectedIntegration == 1
          ? a.efoodId
          : selectedIntegration == 2
            ? a.woltId
            : selectedIntegration == 3
              ? a.boxId
              : selectedIntegration == 5
                ? a.gonnaOrderId
                : selectedIntegration == 6
                  ? a.deliveryManagerId
                  : selectedIntegration == 7
                    ? a.fluterId
                    : a.culizenId;
      const bMatches =
        selectedIntegration == 1
          ? b.efoodId
          : selectedIntegration == 2
            ? b.woltId
            : selectedIntegration == 3
              ? b.boxId
              : selectedIntegration == 5
                ? b.gonnaOrderId
                : selectedIntegration == 6
                  ? b.deliveryManagerId
                  : selectedIntegration == 7
                    ? b.fluterId
                    : b.culizenId;
      if (aMatches && !bMatches) return 1;
      if (!aMatches && bMatches) return -1;
      return 0;
    });
  }, [filteredData, selectedIntegration]);

  useEffect(() => {
    if (!storeId) return;
    getData();
  }, [storeId, selectedIntegration]);

  useEffect(() => {
    if (!storeId) return;
    getData();
  }, [refresh]);

  return (
    <Card className="regular-table-with-color">
      <Card.Header className="mb-2">
        <Card.Title className="row">
          <Col md="6" as="h5">{t("common.labels.storeProducts")}</Col>
          <Col md="6"></Col>
        </Card.Title>
      </Card.Header>
      <Card.Body className="table-responsive">
        {showLoader ? (
          <div style={{ textAlign: "center", marginTop: "5vh", marginBottom: "5vh" }}>
            <ClipLoader size={50} color={"#ff6fbc"} />
          </div>
        ) : (
          <Table className="table-hover" style={{ tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th>
                  ID
                  <Form.Control type="text" placeholder="ID" value={search.id} onChange={(e) => handleSearch(e, "id")} />
                </th>
                <th>{t("tables.headers.category")}<Form.Control type="text" placeholder={t("common.labels.category")} value={search.category} onChange={(e) => handleSearch(e, "category")} />
                </th>
                <th>{t("tables.headers.name")}<Form.Control type="text" placeholder={t("common.labels.name")} value={search.name} onChange={(e) => handleSearch(e, "name")} />
                </th>
                <th>{t("tables.headers.priceEuro")}<Form.Control type="text" placeholder={t("common.labels.price")} value={search.price} onChange={(e) => handleSearch(e, "price")} />
                </th>
                <th className="float-right">{t("tables.headers.matched")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row) => (
                <tr
                  key={row.id}
                  className={selectedRowId == row.id ? "table-success" : ""}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleRowClick(row.id)}
                >
                  <td>{row.id}</td>
                  <td>{row.category.name}</td>
                  <td>{row.name}</td>
                  <td>{row.price.toFixed(2)}</td>
                  <td>
                    <i
                      className={
                        selectedIntegration == 1 && row.efoodId
                          ? "fa fa-check text-success float-right"
                          : selectedIntegration == 2 && row.woltId
                            ? "fa fa-check text-success float-right"
                            : selectedIntegration == 3 && row.boxId
                              ? "fa fa-check text-success float-right"
                              : selectedIntegration == 5 && row.gonnaOrderId
                                ? "fa fa-check text-success float-right"
                                : selectedIntegration == 6 && row.deliveryManagerId
                                  ? "fa fa-check text-success float-right"
                                  : selectedIntegration == 7 && row.fluterId
                                    ? "fa fa-check text-success float-right"
                                    : selectedIntegration == 11 && row.culizenId
                                      ? "fa fa-check text-success float-right"
                                      : "fa fa-times text-danger float-right"
                      }
                    ></i>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

export default StoreProductsTable;
